import { inject, Injectable } from '@angular/core';
import { LoaderService } from '../../../../core/services/loader/loader.service';
import { CommonHttpsService } from '../../../../core/services/https/common-https.service';
import { ToasterMessageService } from '../../../../core/services/topbar-toaster/toaster-message.service';
import { headers } from '../../../../core/common/headers';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { apiUrls } from '../../../../core/common/api_urls/api_urls';
import { GlobalService } from '../../../../core/services/common/global.service';
import { catchError, Subject, tap, throwError } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { session } from '../../../../core/common/session/session';
import { errorMessage } from '../../../../core/common/language/error';

@Injectable({
  providedIn: 'root',
})
export class CareDashboardService {
  public errorMessage = errorMessage;
  public static patient: {
    name: string;
    id: number;
  };

  private refreshSubject = new Subject<void>();

  refresh$ = this.refreshSubject.asObservable();

  triggerRefresh() {
    this.refreshSubject.next();
  }

  constructor(
    public loadingService: LoaderService,
    public commonHttpService: CommonHttpsService,
    public messageService: ToasterMessageService,
    public globalService: GlobalService,
    private http: HttpClient,
  ) {}

  cookiesService = inject(CookieService);

  header = new HttpHeaders({
    [headers.careManagerHeader]: 'true',
  });

  getPatientDetail(id: any) {
    let url = apiUrls.caremanager.dashboard.getPatientDetail(id);
    const header = new HttpHeaders({ [headers.careManagerHeader]: 'true' });

    return this.commonHttpService.GetById(url, header).pipe(
      tap((data) => {}),
      catchError((error: any) => {
        this.messageService.errroMessage(this.errorMessage.ERRORMESSAGE, true);
        this.loadingService.stopSpinner();
        return throwError(() => error);
      }),
    );
  }

  getNewlyConsentedPatients() {
    const userSettings = JSON.parse(this.globalService.getUserSettings());
    const userId = userSettings?.userInfo?.id;
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.dashboard.getNewlyConsentedPatients(userId),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getEscalatedReadings(userId: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.dashboard.getEscalatedPatients(userId),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getNormalReadings(id: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.normalReadingReviews.getNormalReadings(id),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getAdditionalDocuments(id: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.additionalDocuments.getAdditionalDocuments(id),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getMonthlyChartReview(id: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.additionalDocuments.getMonthlyChartReview(id),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getFilters(screenId: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.dashboard.getFilterByScreenId(screenId),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  saveFilters(data: any, screenId: any, userId: any) {
    const bodyData = {
      // practice_id: data?.practice ? data?.practice : null,
      clinic_ids: data?.clinic
        ? data?.clinic.map((item: any) => item.id)
        : null,
      program_ids: data?.program
        ? data?.program.map((item: any) => item.id)
        : null,
      registration_type: data?.registrationType
        ? data?.registrationType.map((item: any) => item.id)
        : null,
      statuses: data?.status ? data?.status.map((item: any) => item.id) : null,
      from_timestamp: data?.dateFrom ? data?.dateFrom : null,
      to_timestamp: data?.dateTo ? data?.dateTo : null,
      screen_id: screenId,
      practice_ids: data?.practice ? [data?.practice] : null,
      cm_id: userId,
      only_selected_devices: data?.onlySelectedDevice
        ? data?.onlySelectedDevice
        : false,
      minimum_selected_devices: data?.minSelectedDevice
        ? data?.minSelectedDevice
        : false,
      device_ids: data.devices
        ? data.devices.map((item: any) => item.id)
        : null,
    };

    // abx = {
    //   twilio_ids: [0],
    //   patient_ids: [0],
    //   user_ids: [0],
    //   cm_id: 0,
    //   device_ids: [0],
    // };

    return this.commonHttpService
      .AddEntity(apiUrls.caremanager.dashboard.saveFilter, bodyData, {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  applyFilters(data: any, screenId: any, userId: any) {
    const bodyData = {
      // practice_id: data?.practice ? data?.practice : null,
      clinic_ids: data?.clinic
        ? data?.clinic.map((item: any) => item.id)
        : null,
      program_ids: data?.program
        ? data?.program.map((item: any) => item.id)
        : null,
      registration_type: data?.registrationType
        ? data?.registrationType.map((item: any) => item.id)
        : null,
      statuses: data?.status ? data?.status.map((item: any) => item.id) : null,
      from_timestamp: data?.dateFrom ? data?.dateFrom : null,
      to_timestamp: data?.dateTo ? data?.dateTo : null,
      screen_id: screenId,
      practice_ids: data?.practice ? [data?.practice] : null,
      cm_id: userId,
      only_selected_devices: data?.onlySelectedDevice
        ? data?.onlySelectedDevice
        : false,
      minimum_selected_devices: data?.minSelectedDevice
        ? data?.minSelectedDevice
        : false,
      device_ids: data.devices
        ? data.devices.map((item: any) => item.id)
        : null,
      patient_name: data?.patient_name ? data.patient_name : null,
    };

    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.dashboard.applyFilter,
        bodyData,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          // this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  //  getNewlyConsentedPatients() {
  //   const userSettings = JSON.parse(this.globalService.getUserSettings());
  //   const userId = userSettings?.userInfo?.id;
  //   return this.commonHttpService
  //     .GetListwHead(
  //       apiUrls.caremanager.dashboard.getNewlyConsentedPatients(userId),
  //       {
  //         headers: this.header,
  //       },
  //     )
  //     .pipe(
  //       tap((data) => {}),
  //       catchError((error: any) => {
  //         this.messageService.errroMessage(error?.message || error, true);
  //         this.loadingService.stopSpinner();
  //         return throwError(() => error);
  //       }),
  //     );
  // }

  getToken() {
    const identity = 'noreply@caregenix.solutions';
    const detail = '1234567890/1/1/2/onboarding';

    let authToken = this.cookiesService.get(session.authToken);
    let user;
    const userSettingsString = localStorage.getItem('userSettings');
    if (userSettingsString) {
      const userSettings = JSON.parse(userSettingsString);
      user = userSettings?.userInfo || {};
    }

    let HEADER;

    if (authToken) {
      HEADER = {
        'X-Token': authToken,
        'X-Username': user?.username,
      };
    }

    return this.http
      .post<{
        token: string;

        cognitoUserId: string;
      }>(
        `${environment.twilioUrl}/api/generate-token`,
        { detail: detail },
        {
          headers: HEADER,
        },
      )
      .pipe(
        tap((data) => {
          localStorage.setItem('cognitoID', data.cognitoUserId);
        }),
        catchError((error: any) => {
          localStorage.setItem('deviceRegistered', 'false');
          return throwError(() => error);
        }),
      );
  }

  getAppointment(id: any, tab_type: string) {
    let url = apiUrls.caremanager.appointment.getAppointmentDetail(
      id,
      tab_type,
    );
    const header = new HttpHeaders({ [headers.careManagerHeader]: 'true' });

    return this.commonHttpService.GetById(url, header).pipe(
      tap((data) => {}),
      catchError((error: any) => {
        this.messageService.errroMessage(this.errorMessage.ERRORMESSAGE, true);
        this.loadingService.stopSpinner();
        return throwError(() => error);
      }),
    );
  }

  cancelAppointment(id: any, tab_type: string) {
    let url = apiUrls.caremanager.appointment.cancelAppointment(id, tab_type);
    const header = new HttpHeaders({ [headers.careManagerHeader]: 'true' });

    return this.commonHttpService.DeleteEntity(url, header).pipe(
      tap((data) => {}),
      catchError((error: any) => {
        this.messageService.errroMessage(this.errorMessage.ERRORMESSAGE, true);
        this.loadingService.stopSpinner();
        return throwError(() => error);
      }),
    );
  }

  getAppointments() {
    let url = apiUrls.caremanager.appointment.getAppointments;
    const header = new HttpHeaders({ [headers.careManagerHeader]: 'true' });

    return this.commonHttpService.GetById(url, header).pipe(
      tap((data) => {}),
      catchError((error: any) => {
        this.messageService.errroMessage(this.errorMessage.ERRORMESSAGE, true);
        this.loadingService.stopSpinner();
        return throwError(() => error);
      }),
    );
  }

  updateAppointmentStatus(form: any) {
    const bodyData = {
      id: form.id,
      status: form.status,
      tab_type: form.tab_type,
    };
    return this.commonHttpService
      .UpdateEntity(
        apiUrls.caremanager.appointment.updateAppointmentStatus,
        bodyData,
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  updateAppointment(form: any) {
    return this.commonHttpService
      .UpdateEntity(apiUrls.caremanager.appointment.updateAppointment, form, {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  normalReadingAction(reading: any, status: string) {
    return this.commonHttpService
      .UpdateEntity(
        apiUrls.caremanager.normalReadingReviews.action(
          reading.readingId,
          status,
          reading.deviceName,
        ),
        {},
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  approveRejectNotes(body: any) {
    return this.commonHttpService
      .UpdateEntity(
        apiUrls.caremanager.additionalDocuments.approveRejectNotes,
        body,
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  updatePatientNotes(note: string, id: any) {
    return this.commonHttpService
      .UpdateEntity(
        apiUrls.caremanager.additionalDocuments.updatePatientNotes(id, note),
        {},
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  discardPatientReading(body: any) {
    return this.commonHttpService
      .PatchEntity(apiUrls.caremanager.dashboard.discardPatient, body, {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.messageService.successMessage(message, true);
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getSummaryBeforeCall(id: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.dashboard.getSummaryBeforeCall(id),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          const Error = error.error;

          const errorMessage = JSON.parse(Error)?.message;
          this.messageService.errroMessage(
            errorMessage ? errorMessage : this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getSummaryAfterCall(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.dashboard.getSummaryAfterCall,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          if (error.status == 503) {
            this.messageService.errroMessage('Summary not Generated', true);
          } else
            this.messageService.errroMessage(
              this.errorMessage.ERRORMESSAGE,
              true,
            );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  saveSummaryAfterCall(body: any) {
    return this.commonHttpService
      .PatchEntity(apiUrls.caremanager.dashboard.saveSummary, body, {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getCCMICR(id: any) {
    return this.commonHttpService
      .GetByIdwHead(
        apiUrls.caremanager.documentation.getPatientCCMICR(id),
        this.header,
      )
      .pipe(
        tap((data) => this.loadingService.stopSpinner()),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getCCMMS(id: any) {
    return this.commonHttpService
      .GetByIdwHead(
        apiUrls.caremanager.documentation.generateCCMMS(id),
        this.header,
      )
      .pipe(
        tap((data) => this.loadingService.stopSpinner()),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getRPMMS(id: any) {
    return this.commonHttpService
      .GetByIdwHead(
        apiUrls.caremanager.documentation.generateRPMMS(id),
        this.header,
      )
      .pipe(
        tap((data) => this.loadingService.stopSpinner()),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getCCMRPMMS(id: any) {
    return this.commonHttpService
      .GetByIdwHead(
        apiUrls.caremanager.documentation.generateMS(id),
        this.header,
      )
      .pipe(
        tap((data) => this.loadingService.stopSpinner()),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getFollowupPatients(id: any) {
    return this.commonHttpService
      .GetListwHead(apiUrls.caremanager.documentation.patientFollowup(id), {
        headers: this.header,
      })
      .pipe(
        tap((data) => this.loadingService.stopSpinner()),
        catchError((error: any) => {
          if (error.status == 404) {
            this.messageService.errroMessage(
              'No patient available in pool',
              true,
            );
          }
          // this.messageService.errroMessage(
          //   this.errorMessage.ERRORMESSAGE,
          //   true,
          // );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getFollowupPendingReadings(id: any) {
    return this.commonHttpService
      .GetListwHead(
        apiUrls.caremanager.documentation.getFollowupPendingReadings(id),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getSummaryAfterCall2(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.generateSummaryAfterCall,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          if (error.status == 503) {
            this.messageService.errroMessage('Summary not Generated', true);
          } else {
            const Error = error.error;

            const errorMessage = JSON.parse(Error)?.message;
            this.messageService.errroMessage(
              errorMessage ? errorMessage : this.errorMessage.ERRORMESSAGE,
              true,
            );
          }
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  saveAiSummary(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.saveAiSummary,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          const Error = error.error;

          const errorMessage = JSON.parse(Error)?.message;
          this.messageService.errroMessage(
            errorMessage ? errorMessage : this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getPatientCountByCMID(id: any) {
    return this.commonHttpService
      .GetByIdwHead(
        apiUrls.caremanager.documentation.getPatientCount(id),
        this.header,
      )
      .pipe(
        tap((data) => {}),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  ApproveFollowUp(patientId: number, timeSpent: number) {
    return this.commonHttpService
      .UpdateEntity(
        apiUrls.caremanager.documentation.approveFollowup(patientId, timeSpent),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            error ? error : this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  ApproveCCMICR(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.approveCCMICR,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  RejectCCMICR(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.rejectCCMICR,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  ApproveCCMMS(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.approveCCMMS,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  RejectCCMMS(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.rejectCCMMS,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  ApproveRPMMS(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.approveRPMMS,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  RejectRPMMS(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.rejectRPMMS,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }
  deleteICDCODECCM(patientId: any, icdCodeId: any) {
    return this.commonHttpService
      .DeleteEntitywHead(
        apiUrls.caremanager.documentation.deleteICDCODECCM(
          patientId,
          icdCodeId,
        ),
        {
          headers: this.header,
        },
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  ApproveMS(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.approveMS,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  RejectMS(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.rejectMS,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  lockPatient(patientId: number) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.lockPatient(patientId),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  unlockPatient(patientId: number) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.unlockPatient(patientId),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }
  unlockCCMRPMPatient(patientId: any, tabName: string) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.documentation.unlockPatientWithTabType(
          patientId,
          tabName,
        ),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  escalateToNP(body: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.np.escalateToNP,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          if (error.error) {
            this.messageService.errroMessage(error.error, true);
          } else
            this.messageService.errroMessage(
              this.errorMessage.ERRORMESSAGE,
              true,
            );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }
  NotificationForNP(patientID: number) {
    let url = apiUrls.caremanager.np.npNotification(patientID);
    const header = new HttpHeaders({ [headers.lookupHeader]: 'true' });
    return this.commonHttpService.GetById(url, header).pipe(
      tap((data) => {this.loadingService.stopSpinner();}),
      catchError((error: any) => {
        this.messageService.errroMessage(this.errorMessage.ERRORMESSAGE, true);
        this.loadingService.stopSpinner();
        return throwError(() => error);
      }),
    );
  }

  getFollowupSummary(id: any) {
    return this.commonHttpService
      .GetListwHead(apiUrls.caremanager.dashboard.getFollowupSummary(id), {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          if (error) {
            this.messageService.errroMessage('No Patients in Pool', true);
          }
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  saveFollowupSummary(
    patientId: any,
    sid: any,
    points: any,
    status: any,
    timetaken: any,
    notes: any,
    program_type: any,
  ) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.dashboard.saveAfterCallSummary(
          patientId,
          sid,
          points,
          status,
          timetaken,
          notes,
          program_type,
        ),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  unlockPoolPatient(poolId: number, tabName: string) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.dashboard.unlockPoolPatient,
        { poolId, tabName },
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getCriticalSummary(id: any) {
    return this.commonHttpService
      .GetListwHead(apiUrls.caremanager.dashboard.getCriticalSummary(id), {
        headers: this.header,
      })
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            'No critical patient available in pool',
            true,
          );
          console.log(error.status)
          if (error.status == 404) {
          }
          // this.messageService.errroMessage(
          //   this.errorMessage.ERRORMESSAGE,
          //   true,
          // );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  saveCriticalSummary(body: any, status: any) {
    return this.commonHttpService
      .AddEntity(
        status == 'Approved'
          ? apiUrls.caremanager.dashboard.approveCriticalSummary
          : apiUrls.caremanager.dashboard.rejectCriticalSummary,
        body,
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  addProgramPatientInteraction(patientId: any, sid: any) {
    return this.commonHttpService
      .AddEntity(
        apiUrls.caremanager.dashboard.addProgramInPatientInteraction(
          patientId,
          sid,
        ),
        {},
        {
          headers: this.header,
        },
        false,
      )
      .pipe(
        tap((message) => {
          this.loadingService.stopSpinner();
        }),
        catchError((error: any) => {
          this.messageService.errroMessage(
            this.errorMessage.ERRORMESSAGE,
            true,
          );
          this.loadingService.stopSpinner();
          return throwError(() => error);
        }),
      );
  }

  getLockStatus(id: any) {
    let url = apiUrls.caremanager.dashboard.getLockstatus(id);
    const header = new HttpHeaders({ [headers.careManagerHeader]: 'true' });

    return this.commonHttpService.GetById(url, header).pipe(
      tap((data) => {}),
      catchError((error: any) => {
        this.messageService.errroMessage(this.errorMessage.ERRORMESSAGE, true);
        this.loadingService.stopSpinner();
        return throwError(() => error);
      }),
    );
  }

    generatePatientPDF(body: any) {
      const url = apiUrls.doctorDashboard.generatePatientPDF;
      const commonHeader = new HttpHeaders({
        [headers.lookupHeader]: 'true',
      });
  
      return this.commonHttpService.http
        .post(url, body, {
          headers: commonHeader,
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(
          tap((data: HttpResponse<Blob>) => {}),
          catchError((error: any) => {
            this.messageService.errroMessage(
              this.errorMessage.ERRORMESSAGE,
              true,
            );
            this.loadingService.stopSpinner();
            return throwError(() => error);
          }),
        );
    }
}
